import React, { useCallback, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Sparkle, CaretDoubleLeft, CaretDoubleRight } from "@phosphor-icons/react";
import {
  AIGradientStrip,
  AIPaneCloseButton,
  AIPaneContainer,
  AIPaneHeader,
  AIPaneHeaderLabel,
  AIPaneHeaderLabelGroup,
  AIPaneTabContent,
  AIPaneTabs,
  InboxTabBadge,
} from "./AIPaneStyled";
import {
  AI_PANE_MODE_INBOX,
  AI_PANE_MODE_MATTER,
  AI_PANE_MODE_MATTER_CHAT_ONLY,
  buildAIPaneSearch,
  buildClosedAIPaneSearch,
  filterTabsByCapability,
  getAIPaneTab,
  getValidTabsForMode,
} from "../../utils/aiPaneUrl";
import { selectSelectedInboxItem } from "../../slice/InboxSlice";
import useAuth from "../../hooks/useAuth";
import BriefingTab from "./BriefingTab";
import AnalysisTab from "./AnalysisTab";
import ChatTab from "./ChatTab";
import ContextAnalysisTab from "./ContextAnalysisTab";
import TasksTab from "./TasksTab";
import MessagesTab from "./MessagesTab";
import MatterWorkPanel from "../WorkItems/MatterWorkPanel";
import CasePlanPanel from "../CasePlan/CasePlanPanel";
import InboxActionFooter, {
  INBOX_ACTION_FOOTER_LAYOUT_RAIL,
  INBOX_ACTION_FOOTER_LAYOUT_ROW,
} from "./InboxActionFooter";
import WaitingOnBanner from "../Inbox/WaitingOnBanner";
import DocVerdictBar from "../Inbox/DocVerdictBar";
import useWaitingOnWorkItems from "../../hooks/useWaitingOnWorkItems";
import useInboxProcessed from "../../hooks/useInboxProcessed";
import { brand } from "../../theme/tokens";

// Per-mode user-facing tab labels.
//
// Matter mode renames the `analysis` tab to "The Brief" (2026-04-29) — the
// internal key stays `analysis` so URLs / Redux keys / file names don't
// have to change. Inbox mode keeps "Briefing" because the inbox briefing
// is its own distinct artifact and the renamed-to-"The Brief" matter tab
// isn't exposed in inbox mode anyway, so there's no naming collision.
//
// `getTabLabel(tab, mode)` reads from this map; `AIPane` consumers should
// always go through it rather than reach into the constant directly so
// future mode-aware label tweaks have a single insertion point.
const TAB_LABELS_BY_MODE = Object.freeze({
  [AI_PANE_MODE_MATTER]: Object.freeze({
    briefing: "Briefing", // Legacy URL fallback only — not in matter tab list.
    analysis: "The Brief",
    chat: "Chat",
    "context-analysis": "Context Analysis",
    tasks: "Tasks",
    messages: "Messages",
    work: "Work",
    plan: "The Plan",
  }),
  // VA CAVC matters: chat is the only surface (no VA Agency graph, so no
  // "The Brief"). The single tab renders without a tab strip at all —
  // labels here only serve the legacy-URL clamp path.
  [AI_PANE_MODE_MATTER_CHAT_ONLY]: Object.freeze({
    chat: "Chat",
  }),
  [AI_PANE_MODE_INBOX]: Object.freeze({
    briefing: "Briefing",
    analysis: "Analysis",
    chat: "Chat",
    "context-analysis": "Context Analysis",
    tasks: "Tasks",
    messages: "Messages",
  }),
});

const getTabLabel = (tab, mode) => {
  const modeMap =
    TAB_LABELS_BY_MODE[mode] || TAB_LABELS_BY_MODE[AI_PANE_MODE_MATTER];
  return modeMap[tab] || tab;
};

/**
 * The shared AI panel.
 *
 * Renders one of two tab subsets depending on `mode`:
 *   - matter (default): [The Brief, Chat]                       — full matter workspace.
 *   - inbox:            [Briefing, Context Analysis, Tasks, Messages] — attorney inbox.
 *
 * Common chrome across modes:
 *   - 3px brand-blue gradient strip ("AI surface" treatment).
 *   - Header with a sparkle glyph, "AI" label, and close button.
 *   - Brand-blue 2px underline indicator on the active tab.
 *
 * URL is the source of truth: `?ai=briefing|analysis|chat|context-analysis|tasks|messages`
 * drives which tab is active. Closing the pane via the X strips the param.
 * The legacy `?chatbox=true` flag is treated as `?ai=chat` on read by
 * `aiPaneUrl`. URLs that name a tab the current mode doesn't expose are
 * clamped to the mode's first tab (e.g. `?ai=chat` on the inbox surface
 * resolves to `briefing` rather than rendering nothing; `?ai=briefing`
 * arriving on matter mode — a legacy bookmark from before the 2026-04-29
 * rename — clamps to `analysis` / "The Brief").
 *
 * The pane only renders its content when the URL says it should be open;
 * the parent layout owner (MatterDetails / InboxPage) still controls the
 * panel slot's visibility so the layout doesn't reserve 500px for a hidden
 * panel.
 *
 * ChatPane owns durable thread hydration and bootstraps a welcome only when
 * the active server-side thread is empty.
 */
const AIPane = ({
  // React 19 dropped defaultProps support for function components, so all
  // optional props are defaulted via destructure here. `mode` defaults to
  // matter so the existing matter-workspace consumers (`MatterDetails`)
  // don't have to thread the new prop everywhere.
  mode = AI_PANE_MODE_MATTER,
  activeDocumentId = null,
  matterId = null,
  clearFactSelectionRef = undefined,
  setActiveTabRef = undefined,
  checkedDocumentIds = [],
  onClearChecked = undefined,
  // Collapsed rail (inbox mode, Matthew 2026-10-08): when the parent
  // supplies `onToggleCollapse`, the header's X becomes a collapse
  // chevron and `collapsed` shrinks the pane to a narrow column that
  // keeps the action buttons (as icons) and hides everything else.
  // Closing the pane outright is still available from the sparkle in
  // the primary sidebar, which strips `?ai=`.
  collapsed = false,
  onToggleCollapse = undefined,
  // Work-items context for the open document, supplied by the matter
  // workspace (Matthew, 2026-10-27). MatterDetails fetches it once and
  // shares it between this pane's verdict bar and the document
  // toolbar's pill, and uses it to open this pane when a document
  // with an open work item lands with the pane closed. Inbox mode
  // leaves it undefined and fetches for itself.
  workItemsContext = undefined,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const canCollapse = typeof onToggleCollapse === "function";
  const isCollapsed = canCollapse && collapsed;
  const isInboxMode = mode === AI_PANE_MODE_INBOX;

  // Pull the inbox row payload (for the action footer's read/snooze
  // state) only when we're in inbox mode. The selector returns null
  // outside the inbox surface, so it's a safe call to mount
  // unconditionally — but we read it conditionally below so matter
  // mode pays nothing for inbox state.
  const inboxItem = useSelector(selectSelectedInboxItem);
  const { hasCapability } = useAuth();

  // Work-items context for the open document's matter — feeds the
  // verdict bar in every mode, plus the WaitingOnBanner and the
  // footer's one-door gate in inbox mode. The inbox fetches for
  // itself; the matter workspace hands the context in via
  // `workItemsContext` so the pane and the document toolbar share one
  // lookup. See useWaitingOnWorkItems for the full story.
  const ownWorkItems = useWaitingOnWorkItems(
    !workItemsContext && isInboxMode && activeDocumentId
      ? activeDocumentId
      : null
  );
  const { waitingOn, reload: reloadWaitingOn } =
    workItemsContext || ownWorkItems;

  // Attaching this document to a work item IS processing it: the volley
  // is back in the requester's court, so the reader shouldn't have to
  // click Mark reviewed as separate bookkeeping. One motion — attach,
  // mark reviewed, advance to the next unread document.
  // A document with no row on the current list page (deep link, usually
  // already-reviewed evidence opened via "Open document") is treated as
  // read: only mark-and-advance when we KNOW it's unread (Bugbot, #641).
  const markProcessed = useInboxProcessed(
    mode === AI_PANE_MODE_INBOX ? activeDocumentId : null,
    inboxItem ? Boolean(inboxItem.is_read) : true
  );
  const handleWorkItemAttached = useCallback(() => {
    reloadWaitingOn();
    markProcessed();
  }, [reloadWaitingOn, markProcessed]);

  // Capability-narrowed tab list (2026-08-05 access-control rollout):
  // e.g. a user without `ai.brief.view` never gets an "analysis" tab
  // item at all, regardless of what's in the URL. VA CAVC matters
  // (`AI_PANE_MODE_MATTER_CHAT_ONLY`) bypass this — see
  // `filterTabsByCapability`.
  const validTabs = useMemo(
    () =>
      filterTabsByCapability(getValidTabsForMode(mode), {
        mode,
        hasCapability,
        hasActiveDocument: Boolean(activeDocumentId),
      }),
    [mode, hasCapability, activeDocumentId]
  );
  const rawTab = useMemo(() => getAIPaneTab(location.search), [location.search]);
  // Clamp against the capability-narrowed list, not the mode's full
  // list — a URL naming a tab the user isn't allowed to see (bookmark,
  // stale link, or a capability that was since revoked) falls back to
  // the first tab actually available rather than rendering a hidden one.
  const activeTab = useMemo(() => {
    if (!rawTab) return null;
    if (validTabs.includes(rawTab)) return rawTab;
    return validTabs[0] || null;
  }, [rawTab, validTabs]);

  // Keep the latest `location` in a ref so the navigate-callback
  // identities below stay stable across URL changes. Bugbot caught
  // that closing over `location.pathname` / `location.search`
  // directly produced a fresh `navigateToTab` on every URL change
  // (folder navigation, document switches, etc.), which propagated
  // through `renderTabBody` → `tabItems` and rebuilt JSX for ALL
  // three tab children even when only the active document changed.
  // The chat tab in particular shouldn't re-render on document
  // navigation. By reading from the ref, the callbacks only depend
  // on the truly-stable `navigate` from React Router v6.
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  });

  const navigateToTab = useCallback(
    (nextTab) => {
      const { pathname, search } = locationRef.current;
      const nextSearch = buildAIPaneSearch(search, nextTab);
      navigate(`${pathname}${nextSearch}`, { replace: true });
    },
    [navigate]
  );

  const handleTabChange = useCallback(
    (nextTab) => {
      navigateToTab(nextTab);
    },
    [navigateToTab]
  );

  const handleClose = useCallback(() => {
    const { pathname, search } = locationRef.current;
    const nextSearch = buildClosedAIPaneSearch(search);
    navigate(`${pathname}${nextSearch}`, { replace: true });
  }, [navigate]);

  const renderTabBody = useCallback(
    (tab) => {
      const isActive = tab === activeTab;
      if (tab === "briefing") {
        return (
          <BriefingTab
            documentId={activeDocumentId}
            onSwitchTab={navigateToTab}
            isActive={isActive}
          />
        );
      }
      if (tab === "analysis") {
        return (
          <AnalysisTab
            documentId={activeDocumentId}
            matterId={matterId}
            isActive={isActive}
          />
        );
      }
      if (tab === "chat") {
        return (
          <ChatTab
            clearFactSelectionRef={clearFactSelectionRef}
            setActiveTabRef={setActiveTabRef}
            documentId={activeDocumentId}
          />
        );
      }
      if (tab === "context-analysis") {
        return (
          <ContextAnalysisTab
            documentId={activeDocumentId}
            isActive={isActive}
          />
        );
      }
      if (tab === "tasks") {
        return (
          <TasksTab documentId={activeDocumentId} isActive={isActive} />
        );
      }
      if (tab === "messages") {
        return (
          <MessagesTab
            documentId={activeDocumentId}
            isActive={isActive}
            threadId={location.state?.messageThreadId}
          />
        );
      }
      if (tab === "work") {
        // Matter mode only (see TABS_BY_MODE): the matter's work items,
        // stage checklist, reminders, and waiting-on-outside — the WHG
        // Docs twin of the new Salesforce work rail.
        return <MatterWorkPanel matterId={matterId} isActive={isActive} />;
      }
      if (tab === "plan") {
        // Matter mode only: the plan of record — the attorney's standing
        // strategy as data (issues, lanes, branches, open loops), with
        // Approve for drafts the memo engine proposed.
        return <CasePlanPanel matterId={matterId} isActive={isActive} />;
      }
      return null;
    },
    [activeDocumentId, activeTab, clearFactSelectionRef, location.state?.messageThreadId, matterId, navigateToTab, setActiveTabRef]
  );

  // We deliberately keep every mode-visible tab body mounted across
  // switches (no `destroyInactiveTabPane`). Chat owns conversation
  // state in local React state inside `ChatPane` — destroying its
  // mount on each tab switch would lose the conversation history and
  // leave the user stuck on the welcome loader after their first
  // message. Briefing / Analysis / Context Analysis / Tasks / Messages
  // each drive their own retry-on-activation logic via the `isActive`
  // prop, which Bugbot earlier confirmed gives us the same "free
  // retry on tab switch" semantics for empty/failed states without
  // depending on remount.
  //
  // Tab list is mode-derived so the same component renders
  // [The Brief, Chat] in matter mode (the "analysis" tab body
  // labelled "The Brief" + chat) and [Briefing, Context Analysis,
  // Tasks, Messages] in inbox mode without conditional JSX
  // duplication. Inbox-mode Tasks / Messages tab labels carry a
  // numeric badge (open task count + unread message count) sourced
  // from the inbox row payload — counts update optimistically as
  // the user mutates state via the action footer or per-row PATCH
  // controls, and reconcile on the next list refetch.
  const openTaskCount = useMemo(() => {
    if (mode !== AI_PANE_MODE_INBOX) return 0;
    const n = Number(inboxItem?.open_task_count);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [inboxItem, mode]);

  const unreadMessageCount = useMemo(() => {
    if (mode !== AI_PANE_MODE_INBOX) return 0;
    const n = Number(inboxItem?.unread_message_count);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [inboxItem, mode]);

  const tabItems = useMemo(
    () =>
      validTabs.map((key) => {
        const baseLabel = getTabLabel(key, mode);
        let label = baseLabel;
        if (key === "tasks" && openTaskCount > 0) {
          label = (
            <span>
              {baseLabel}
              <InboxTabBadge $tone="neutral" aria-label={`${openTaskCount} open tasks`}>
                {openTaskCount}
              </InboxTabBadge>
            </span>
          );
        } else if (key === "messages" && unreadMessageCount > 0) {
          label = (
            <span>
              {baseLabel}
              <InboxTabBadge $tone="unread" aria-label={`${unreadMessageCount} unread messages`}>
                {unreadMessageCount}
              </InboxTabBadge>
            </span>
          );
        }
        return {
          key,
          label,
          children: <AIPaneTabContent>{renderTabBody(key)}</AIPaneTabContent>,
        };
      }),
    [mode, openTaskCount, renderTabBody, unreadMessageCount, validTabs]
  );

  // If the URL says the pane is closed we render nothing. The parent
  // wrapper hides its 500px slot independently so layout doesn't shift.
  if (!activeTab) return null;

  // Single-tab modes (VA CAVC chat-only) hide the tab strip — a
  // one-item strip is dead chrome (Matthew, 2026-07-16). The strip is
  // hidden via CSS on the SAME AIPaneTabs wrapper rather than swapping
  // to a different container: the mode is derived from async matter
  // details and can flip after mount, and a wrapper swap would unmount
  // the chat subtree and drop any conversation started during the
  // loading window (Bugbot 265/R2).
  const isSingleTabMode = tabItems.length === 1;

  return (
    <AIPaneContainer
      aria-label="AI assistant panel"
      data-collapsed={isCollapsed ? "true" : undefined}
    >
      <AIGradientStrip aria-hidden="true" />
      <AIPaneHeader $collapsed={isCollapsed}>
        {isCollapsed ? (
          <AIPaneCloseButton
            type="button"
            onClick={onToggleCollapse}
            aria-label="Expand AI assistant"
            aria-expanded="false"
            title="Expand"
          >
            <CaretDoubleLeft size={16} weight="bold" />
          </AIPaneCloseButton>
        ) : (
          <>
            <AIPaneHeaderLabelGroup>
              <Sparkle
                size={16}
                weight="fill"
                color={brand.primary}
                aria-hidden="true"
              />
              <AIPaneHeaderLabel>AI</AIPaneHeaderLabel>
            </AIPaneHeaderLabelGroup>
            {canCollapse ? (
              <AIPaneCloseButton
                type="button"
                onClick={onToggleCollapse}
                aria-label="Collapse AI assistant"
                aria-expanded="true"
                title="Collapse"
              >
                <CaretDoubleRight size={16} weight="bold" />
              </AIPaneCloseButton>
            ) : (
              <AIPaneCloseButton
                type="button"
                onClick={handleClose}
                aria-label="Close AI assistant"
              >
                <X size={16} weight="bold" />
              </AIPaneCloseButton>
            )}
          </>
        )}
      </AIPaneHeader>
      {/*
        Verdict bar (green): the open work item this document belongs
        to, with the holder's Return / Comment / Snooze and the
        requester's Accept & close / Send back — beside the document,
        so the review happens where the evidence is on screen. In the
        inbox it reads "This document answers" with the matter named;
        inside a matter the matter is already on screen, so the bar
        reads "Work item on this document" and lets the card attach
        files from this matter. The matter workspace opens this pane
        on landing when the document has an open work item (Matthew,
        2026-10-27: the work item must be visible WHILE reading, never
        stacked above the PDF where it steals height).
      */}
      {activeDocumentId && !isCollapsed && (
        <DocVerdictBar
          items={waitingOn?.linked || []}
          onActed={reloadWaitingOn}
          title={isInboxMode ? undefined : "Work item on this document"}
          showMatter={isInboxMode}
          matterId={isInboxMode ? undefined : matterId}
        />
      )}
      {/*
        Conveyor-belt banner (inbox mode only): when this document's
        matter has open work items waiting on something inbound, say so
        right here — the reader holding the paper is the one who can
        close the loop. Renders nothing when nothing is waiting. Matter
        mode has the same information in the Work tab's "waiting on
        outside" lane, so this banner would be a duplicate there.
      */}
      {mode === AI_PANE_MODE_INBOX && activeDocumentId && !isCollapsed && (
        <WaitingOnBanner
          documentId={activeDocumentId}
          items={waitingOn?.items || []}
          onAttached={handleWorkItemAttached}
        />
      )}
      {/*
        Tabs stay mounted while collapsed (hidden via CSS) so expanding
        brings back exactly the tab state the user left — see the
        "keep every tab mounted" note above.
      */}
      <AIPaneTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        $hideTabBar={isSingleTabMode}
        $collapsed={isCollapsed}
      />
      {/*
        Pinned action footer (inbox mode only). The doc id and row
        payload are wired here so the footer's "Mark reviewed" /
        "Snooze" reflect server state (`is_read`, `snoozed_until`)
        without prop-drilling through every AIPane consumer. Matter
        mode renders no footer — the matter workspace's existing
        toolbar covers per-doc actions there. When the rail is
        collapsed the same footer renders as a vertical icon column.
      */}
      {mode === AI_PANE_MODE_INBOX && activeDocumentId && (
        <InboxActionFooter
          documentId={activeDocumentId}
          item={inboxItem}
          hideCreateTask={Boolean(waitingOn?.matter_found)}
          onWorkItemAttached={handleWorkItemAttached}
          checkedDocumentIds={checkedDocumentIds}
          onClearChecked={onClearChecked}
          layout={
            isCollapsed
              ? INBOX_ACTION_FOOTER_LAYOUT_RAIL
              : INBOX_ACTION_FOOTER_LAYOUT_ROW
          }
        />
      )}
    </AIPaneContainer>
  );
};

AIPane.propTypes = {
  mode: PropTypes.oneOf([
    AI_PANE_MODE_MATTER,
    AI_PANE_MODE_MATTER_CHAT_ONLY,
    AI_PANE_MODE_INBOX,
  ]),
  activeDocumentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clearFactSelectionRef: PropTypes.shape({
    current: PropTypes.func,
  }),
  setActiveTabRef: PropTypes.shape({
    current: PropTypes.func,
  }),
  checkedDocumentIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onClearChecked: PropTypes.func,
  collapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  workItemsContext: PropTypes.shape({
    waitingOn: PropTypes.object,
    reload: PropTypes.func.isRequired,
  }),
};

export default AIPane;
